import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchContracts } from '../../api/contract';
import { useCompanyStore } from '../../store/companyStore';
import { canManageContracts } from '../../types/companyRole';
import { Contract, CONTRACT_STAGES } from '../../types/contract';

import ContractDetail from './ContractDetail';
import ContractForm from './ContractForm';
import ContractPendingDeletion from './ContractPendingDeletion';
import './ContractPanel.css';

import { reportError } from '../../utils/notify';
type PanelView =
  | { type: 'list' }
  | { type: 'create' }
  | { type: 'detail'; contractId: number }
  | { type: 'edit'; contractId: number }
  | { type: 'pending-deletion' }
  | { type: 'pending-detail'; contractId: number };

const LIST_VIEW: PanelView = { type: 'list' };

function ContractPanel({ initialContractId = null }: { initialContractId?: number | null }) {
  const companyId = useCompanyStore((state) => state.companyId);
  const role = useCompanyStore((state) => state.role);
  const isManager = canManageContracts(role);

  const [view, setView] = useState<PanelView>(LIST_VIEW);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [stageFilter, setStageFilter] = useState<string>('ALL');
  const [urgentOnly, setUrgentOnly] = useState(false);
  const [draftOnly, setDraftOnly] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadContracts = useCallback(async (keyword?: string) => {
    try {
      setLoading(true);
      const list = await fetchContracts(keyword);
      setContracts(list);
      setError('');
    } catch {
      reportError(setError, '계약서 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setView(LIST_VIEW);
    setSelectedId(null);
    setSearchKeyword('');
    setStageFilter('ALL');
    setUrgentOnly(false);
    setDraftOnly(false);
    loadContracts();
  }, [companyId, loadContracts, setView]);

  useEffect(() => {
    if (initialContractId == null) return;
    setSelectedId(initialContractId);
    setView({ type: 'detail', contractId: initialContractId });
  }, [initialContractId]);

  useEffect(() => {
    if (view.type !== 'list') return;
    const timer = window.setTimeout(() => {
      loadContracts(searchKeyword);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [searchKeyword, loadContracts, view.type]);

  const filteredContracts = useMemo(() => {
    return contracts.filter((contract) => {
      if (stageFilter !== 'ALL' && contract.currentStage !== stageFilter) {
        return false;
      }
      if (urgentOnly && !contract.urgent) {
        return false;
      }
      if (draftOnly && !contract.draft) {
        return false;
      }
      return true;
    });
  }, [contracts, stageFilter, urgentOnly, draftOnly]);

  const selectedContract = useMemo(
    () => filteredContracts.find((item) => item.contractId === selectedId) ?? null,
    [filteredContracts, selectedId]
  );

  const stageOptions = useMemo(() => {
    const names = new Set<string>(CONTRACT_STAGES);
    contracts.forEach((contract) => {
      if (contract.currentStage) names.add(contract.currentStage);
      contract.stages?.forEach((stage) => {
        if (stage.stageName) names.add(stage.stageName);
      });
    });
    return Array.from(names);
  }, [contracts]);

  const goList = () => {
    setView(LIST_VIEW);
    void loadContracts(searchKeyword);
  };

  return (
    <main className="contract-panel">
      <aside className="contract-panel__sidebar">
        <div className="contract-panel__side-header">
          <strong>계약서</strong>
          <span>
            {view.type === 'pending-deletion' || view.type === 'pending-detail'
              ? '삭제 예정'
              : '전체 목록'}
          </span>
        </div>

        <div className="contract-panel__nav">
          <button
            type="button"
            className={
              view.type !== 'pending-deletion' && view.type !== 'pending-detail' ? 'is-active' : ''
            }
            onClick={() => {
              setSelectedId(null);
              setView(LIST_VIEW);
            }}
          >
            계약 목록
          </button>
          {isManager && (
            <button
              type="button"
              className={
                view.type === 'pending-deletion' || view.type === 'pending-detail' ? 'is-active' : ''
              }
              onClick={() => {
                setSelectedId(null);
                setView({ type: 'pending-deletion' });
              }}
            >
              삭제 예정
            </button>
          )}
        </div>

        {view.type !== 'pending-deletion' && view.type !== 'pending-detail' && (
          <>
            <div className="contract-panel__search">
              <input
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                placeholder="문서번호 / 계약번호 / 계약명 / 발주처 검색"
              />
            </div>

            <div className="contract-panel__filters">
              <button
                type="button"
                className={stageFilter === 'ALL' ? 'is-active' : ''}
                onClick={() => setStageFilter('ALL')}
              >
                전체
              </button>
              {stageOptions.map((stage) => (
                <button
                  key={stage}
                  type="button"
                  className={stageFilter === stage ? 'is-active' : ''}
                  onClick={() => setStageFilter(stage)}
                >
                  {stage}
                </button>
              ))}
              <button
                type="button"
                className={`is-urgent-filter${urgentOnly ? ' is-active' : ''}`}
                onClick={() => setUrgentOnly((prev) => !prev)}
              >
                긴급만
              </button>
              <button
                type="button"
                className={`is-draft-filter${draftOnly ? ' is-active' : ''}`}
                onClick={() => setDraftOnly((prev) => !prev)}
              >
                임시만
              </button>
            </div>

            <div className="contract-panel__list-wrap">
              {loading ? (
                <p className="contract-panel__hint">불러오는 중...</p>
              ) : filteredContracts.length === 0 ? (
                <p className="contract-panel__hint">표시할 계약서가 없습니다.</p>
              ) : (
                <ul className="contract-panel__list">
                  <li>
                    <button
                      type="button"
                      className={`contract-panel__list-item${
                        view.type === 'list' && selectedId == null ? ' is-active' : ''
                      }`}
                      onClick={() => {
                        setSelectedId(null);
                        setView(LIST_VIEW);
                      }}
                    >
                      전체 보기 ({filteredContracts.length})
                    </button>
                  </li>
                  {filteredContracts.map((contract) => (
                    <li key={contract.contractId}>
                      <button
                        type="button"
                        className={`contract-panel__list-item${
                          selectedId === contract.contractId ? ' is-active' : ''
                        }${contract.urgent ? ' is-urgent' : ''}`}
                        onClick={() => {
                          setSelectedId(contract.contractId);
                          setView(LIST_VIEW);
                        }}
                        onDoubleClick={() => {
                          setSelectedId(contract.contractId);
                          setView({ type: 'detail', contractId: contract.contractId });
                        }}
                      >
                        <span className="contract-panel__list-name">
                          {contract.draft ? '[임시] ' : ''}
                          {contract.urgent ? '[긴급] ' : ''}
                          {contract.contractName}
                        </span>
                        <span className="contract-panel__list-meta">
                          {contract.documentNo ? `${contract.documentNo} · ` : ''}
                          {contract.contractNo ? `${contract.contractNo} · ` : ''}
                          {contract.clientName || '발주처 미입력'} · {contract.currentStage || '-'}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </aside>

      <section className="contract-panel__main">
        {view.type === 'pending-deletion' ? (
          <ContractPendingDeletion
            onRestored={() => void loadContracts(searchKeyword)}
            onOpenDetail={(contractId) => setView({ type: 'pending-detail', contractId })}
          />
        ) : view.type === 'pending-detail' ? (
          <ContractDetail
            contractId={view.contractId}
            pendingMode
            onBack={() => setView({ type: 'pending-deletion' })}
            onRestored={() => {
              void loadContracts(searchKeyword);
              setView({ type: 'pending-deletion' });
            }}
          />
        ) : view.type === 'create' ? (
          <ContractForm
            mode="create"
            onCancel={goList}
            onSaved={(saved) => {
              setSelectedId(saved.contractId);
              setView({ type: 'detail', contractId: saved.contractId });
              void loadContracts(searchKeyword);
            }}
          />
        ) : view.type === 'edit' ? (
          <ContractForm
            mode="edit"
            contractId={view.contractId}
            onCancel={() => setView({ type: 'detail', contractId: view.contractId })}
            onSaved={(saved) => {
              setSelectedId(saved.contractId);
              setView({ type: 'detail', contractId: saved.contractId });
              void loadContracts(searchKeyword);
            }}
          />
        ) : view.type === 'detail' ? (
          <ContractDetail
            contractId={view.contractId}
            onBack={goList}
            onEdit={() => setView({ type: 'edit', contractId: view.contractId })}
            onDeleted={goList}
          />
        ) : (
          <>
            <div className="contract-panel__toolbar">
              <div>
                <h2>계약서 현황</h2>
                <p>
                  {selectedContract
                    ? `선택: ${selectedContract.contractName} (더블클릭 시 상세)`
                    : `표시 ${filteredContracts.length}건 / 전체 ${contracts.length}건 · 행 더블클릭으로 상세`}
                </p>
              </div>
              <button
                type="button"
                className="contract-panel__primary"
                onClick={() => setView({ type: 'create' })}
              >
                계약서 등록
              </button>
            </div>

            {error && <p className="contract-panel__error">{error}</p>}

            <div className="contract-panel__content">
              {loading ? (
                <p className="contract-panel__hint">불러오는 중...</p>
              ) : filteredContracts.length === 0 ? (
                <p className="contract-panel__hint">계약서를 등록해 주세요.</p>
              ) : (
                <table className="ec-table contract-panel__table">
                  <thead>
                    <tr>
                      <th style={{ width: '48px' }}>순서</th>
                      <th style={{ width: '120px' }}>문서번호</th>
                      <th style={{ width: '110px' }}>계약번호</th>
                      <th>계약명</th>
                      <th style={{ width: '120px' }}>발주처</th>
                      <th style={{ width: '150px' }}>계약기간</th>
                      <th style={{ width: '100px' }}>납품예정일</th>
                      <th style={{ width: '90px' }}>사내담당자</th>
                      <th style={{ width: '80px' }}>단계</th>
                      <th style={{ width: '100px' }}>금액</th>
                      <th style={{ width: '80px' }}>등록자</th>
                      <th style={{ width: '80px' }}>수정자</th>
                      <th style={{ width: '70px' }}>상태</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredContracts.map((contract, index) => (
                      <tr
                        key={contract.contractId}
                        className={[
                          selectedId === contract.contractId ? 'is-selected' : '',
                          contract.urgent ? 'is-urgent' : '',
                          contract.draft ? 'is-draft' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        onClick={() => setSelectedId(contract.contractId)}
                        onDoubleClick={() =>
                          setView({ type: 'detail', contractId: contract.contractId })
                        }
                      >
                        <td>{(contract.sortOrder ?? index) + 1}</td>
                        <td>{contract.documentNo || '-'}</td>
                        <td>{contract.contractNo || '-'}</td>
                        <td>{contract.contractName}</td>
                        <td>{contract.clientName || '-'}</td>
                        <td>{formatPeriod(contract.startDate, contract.endDate)}</td>
                        <td>{contract.deliveryDueDate || '-'}</td>
                        <td>{contract.managerName || '-'}</td>
                        <td>
                          <span className="contract-panel__stage">
                            {contract.currentStage || '-'}
                          </span>
                        </td>
                        <td className="contract-panel__amount">
                          {formatWon(contract.totalAmount ?? 0)}
                        </td>
                        <td>{contract.createdUserName || '-'}</td>
                        <td>{contract.lastUpdatedUserName || '-'}</td>
                        <td>{contract.draft ? '임시' : contract.urgent ? '긴급' : '정상'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </section>
    </main>
  );
}

function formatPeriod(start?: string | null, end?: string | null) {
  if (!start && !end) return '-';
  return `${start || '?'} ~ ${end || '?'}`;
}

function formatWon(value: number) {
  return `${value.toLocaleString('ko-KR')}원`;
}

export default ContractPanel;
